import { observer } from 'mobx-react-lite'
import {
  AllJobsView,
  AllJobsViewContent,
  AllJobsViewContentSkeleton,
  AllJobsViewEmptyState,
  AllJobsViewJobList,
  AllJobsViewJobListItem,
  AllJobsViewJobListItemAvatarDecoration,
  AllJobsViewJobListItemDefaultDecoration,
  AllJobsViewJobListItemDraftActionsMenu,
  AllJobsViewJobListItemLabelChip,
  AllJobsViewJobListItemSkeleton,
  AllJobsViewScreenHeader,
  AllJobsViewSection,
  AllJobsViewSectionHeadline,
} from '@/features/jobs/list/components/AllJobsView/index'
import { useTranslate } from '@/hooks/translate'
import { Button } from '@/components/Button'
import { useFeatures } from '@/contexts/root-store-context'
import { useActivation } from '@/hooks/view-model-hooks'
import type { JobListItemViewModel } from '@/features/jobs/list/JobListItemViewModel'
import { dateFormatters } from '@/utils/date-util'
import { links } from '@/utils/link'
import { getStatusDisplayHeadline } from '@/features/jobs/job-status-display'
import { SkeletonList } from '@/components/Skeleton'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'

/**
 * The AllJobsViewContainer
 */
export default observer(function AllJobsViewContainer() {
  const T = useTranslate()
  const startNewLabel = T('Web.Dashboard.Button.New', 'Start new service')

  const { internal, getCurrentMember } = useFeatures(({ jobs, iam }) => ({
    internal: jobs.internal(),
    getCurrentMember: iam.getCurrentMember,
  }))
  const vm = internal.allJobsVm
  const member = getCurrentMember()

  useActivation(vm)

  // Render a skeleton for past jobs until we've fully paged
  // through the current jobs.
  const isSkeletonPastJobs =
    !vm.currentJobListVm.scroller.isDone ||
    vm.completedJobListVm.scroller.fetch.pending

  const isShowingCurrentJobs =
    !vm.currentJobListVm.scroller.isDone || vm.currentJobs.length > 0

  const isShowingPastJobs =
    !vm.completedJobListVm.scroller.isDone || vm.completedJobs.length > 0

  return (
    <AllJobsView>
      <AllJobsViewScreenHeader />
      {vm.currentJobListVm.isFetchingInitial ||
      vm.completedJobListVm.isFetchingInitial ? (
        <AllJobsViewContentSkeleton />
      ) : (
        <AllJobsViewContent>
          {vm.isEmpty && (
            <AllJobsViewEmptyState
              isStartNewAllowed={member.permissions.startNewJob}
              startNewLabel={startNewLabel}
            />
          )}

          {!vm.isEmpty && (
            <>
              {isShowingCurrentJobs && (
                <AllJobsViewSection>
                  <AllJobsViewSectionHeadline
                    count={vm.totalCurrentJobs}
                    action={
                      member.permissions.startNewJob && (
                        <Button
                          variant="text"
                          iconLeft="add"
                          label={startNewLabel}
                          to={links.startNewService()}
                        />
                      )
                    }
                  >
                    Current
                  </AllJobsViewSectionHeadline>
                  <AllJobsViewJobList>
                    {vm.currentJobs.map((jobVm) => (
                      <AllJobsViewListItemContainer
                        key={jobVm.job.id}
                        vm={jobVm}
                      />
                    ))}
                    <SkeletonListInfiniteScrollTrigger
                      scroller={vm.currentJobListVm.scroller}
                      attachRefTo="all"
                      maxSkeletons={5}
                    >
                      <AllJobsViewJobListItemSkeleton withChip />
                    </SkeletonListInfiniteScrollTrigger>
                  </AllJobsViewJobList>
                </AllJobsViewSection>
              )}

              {isShowingPastJobs && (
                <AllJobsViewSection>
                  <AllJobsViewSectionHeadline
                    count={vm.totalCompletedJobs}
                    isSkeleton={isSkeletonPastJobs}
                    action={
                      !isShowingCurrentJobs && (
                        <Button
                          variant="text"
                          iconLeft="add"
                          label={startNewLabel}
                        />
                      )
                    }
                  >
                    Past
                  </AllJobsViewSectionHeadline>

                  {isSkeletonPastJobs ? (
                    <AllJobsViewJobList>
                      <SkeletonList count={4}>
                        <AllJobsViewJobListItemSkeleton />
                      </SkeletonList>
                    </AllJobsViewJobList>
                  ) : (
                    <AllJobsViewJobList>
                      {vm.completedJobs.map((jobVm) => (
                        <AllJobsViewListItemContainer
                          key={jobVm.job.id}
                          vm={jobVm}
                        />
                      ))}
                      <SkeletonListInfiniteScrollTrigger
                        scroller={vm.completedJobListVm.scroller}
                        attachRefTo="all"
                        maxSkeletons={5}
                      >
                        <AllJobsViewJobListItemSkeleton />
                      </SkeletonListInfiniteScrollTrigger>
                    </AllJobsViewJobList>
                  )}
                </AllJobsViewSection>
              )}
            </>
          )}
        </AllJobsViewContent>
      )}
    </AllJobsView>
  )
})

interface AllJobsViewListItemContainerProps {
  vm: JobListItemViewModel
}

/**
 * All Jobs View List Item Container
 */
const AllJobsViewListItemContainer = observer(
  function AllJobsViewListItemContainer({
    vm,
  }: AllJobsViewListItemContainerProps) {
    const { job } = vm

    switch (job.status) {
      case 'UnderConstruction': {
        return (
          <AllJobsViewJobListItem
            title={job.displayName}
            subtitle={`Last modified on ${dateFormatters.dateTimeFull.format(job.lastActivityAt)}`}
            decoration={<AllJobsViewJobListItemDecorationContainer vm={vm} />}
            to={links.editDraftJob(job.id)}
            actions={
              <AllJobsViewJobListItemDraftActionsMenu
                isPending={vm.promptDelete.pending}
                to={links.jobDetails(job.id)}
                onDeletePress={vm.promptDelete}
              />
            }
            labelChip={
              <AllJobsViewJobListItemLabelChip variant="neutral" text="Draft" />
            }
          />
        )
      }
      case 'Closed': {
        return (
          <AllJobsViewJobListItem
            to={links.jobDetails(job.id)}
            title={job.displayName}
            subtitle={`Completed on ${dateFormatters.dateTimeFull.format(job.completedAt ?? job.lastActivityAt)}`}
            decoration={<AllJobsViewJobListItemDecorationContainer vm={vm} />}
          />
        )
      }
      default: {
        return (
          <AllJobsViewJobListItem
            to={links.jobDetails(job.id)}
            title={job.displayName}
            subtitle={`Last modified on ${dateFormatters.dateTimeFull.format(job.lastActivityAt)}`}
            decoration={<AllJobsViewJobListItemDecorationContainer vm={vm} />}
            labelChip={
              <AllJobsViewJobListItemLabelChip
                variant="default"
                text={getStatusDisplayHeadline({ status: job.status })}
              />
            }
          />
        )
      }
    }
  },
)

interface AllJobsViewJobListItemDecorationContainerProps {
  vm: JobListItemViewModel
}

/**
 * All Jobs View Job List Item Decoration Container
 */
const AllJobsViewJobListItemDecorationContainer = observer(
  function AllJobsViewJobListItemDecorationContainer({
    vm,
  }: AllJobsViewJobListItemDecorationContainerProps) {
    const { job } = vm
    const isCompleted = job.status === 'Closed'
    return job.championPro ? (
      <AllJobsViewJobListItemAvatarDecoration
        displayName={job.championPro.user.displayName}
        avatarUrl={job.championPro.user.avatarUrl}
        badgeCount={vm.unreadCount}
        isCompleted={isCompleted}
        presence={
          isCompleted
            ? 'none'
            : job.championPro.user.isOnline
              ? 'online'
              : 'offline'
        }
      />
    ) : (
      <AllJobsViewJobListItemDefaultDecoration
        badgeCount={vm.unreadCount}
        isCompleted={isCompleted}
      />
    )
  },
)
